import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import CheckIcon from '@mui/icons-material/Check';
import FlagIcon from '@mui/icons-material/Flag';
import {
  displayText,
  formatDate,
  isoWeek,
  mondayOf,
  phaseColor,
  relativeDue,
  scheduleStatusText,
  shortDate,
} from './helpers';
import { help } from './helpTexts';
import { chipButtonSx, launchColors, scheduleMarkerColor } from './launchTheme';
import {
  buildAxis,
  buildTimelineGroups,
  dayCenter,
  placeDate,
  TIMELINE_LABEL_WIDTH,
  TimelineFilter,
  TimelineItem,
  timelineWindow,
  TimelineZoom,
  zoomPxPerDay,
} from './timelineLayout';
import DepartmentBadge from './ui/DepartmentBadge';
import { ActivityProjection, Timeline } from './types';
import InfoTip from './ui/InfoTip';

interface Props {
  timeline: Timeline;
  activities?: ActivityProjection[];
  currentUserId?: string;
  myDepartments?: string[];
  onOpenActivity: (activityId: string) => void;
}

const LABEL = TIMELINE_LABEL_WIDTH;
const PHASE_ROW = 40;
const ACTIVITY_ROW = 36;
const LANE = 22;

const filterOptions: Array<{ value: TimelineFilter; label: string }> = [
  { value: 'all', label: 'Alla' },
  { value: 'open', label: 'Endast öppna' },
  { value: 'mine', label: 'Min avdelning' },
];

const legend: Array<{ status: keyof typeof scheduleMarkerColor; label: string }> = [
  { status: 'overdue', label: 'Försenad' },
  { status: 'at_risk', label: 'Risk' },
  { status: 'due_soon', label: 'Snart' },
  { status: 'on_track', label: 'Enligt plan' },
  { status: 'complete', label: 'Klar' },
];

const TimelineView: React.FC<Props> = ({ timeline, activities = [], currentUserId = '', myDepartments = [], onOpenActivity }) => {
  const [chainId, setChainId] = useState(timeline.announcements[0]?.chainId || '');
  const [zoom, setZoom] = useState<TimelineZoom>('month');
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const groups = useMemo(
    () => buildTimelineGroups(timeline, activities, filter, currentUserId, myDepartments),
    [activities, currentUserId, filter, myDepartments, timeline],
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const span = useMemo(() => timelineWindow(timeline, zoom), [timeline, zoom]);
  const chain = timeline.announcements.find((item) => item.chainId === chainId) || timeline.announcements[0] || null;
  const axis = useMemo(() => buildAxis(span, chain?.date), [chain?.date, span]);
  const px = zoomPxPerDay[zoom];
  const plotWidth = span.days * px;
  const today = placeDate(timeline.asOf, span);
  const todayX = today.inside ? dayCenter(today.day, px) : null;

  const bands = useMemo(() => {
    const placed = [...timeline.announcements]
      .sort((left, right) => left.date.localeCompare(right.date))
      .map((item) => {
        const weekStart = mondayOf(item.date);
        const start = placeDate(weekStart < span.start ? span.start : weekStart, span);
        return { ...item, x: start.inside ? start.day * px : null, width: 7 * px };
      })
      .filter((item): item is typeof item & { x: number } => item.x != null);
    const laneEnds: number[] = [];
    return placed.map((item) => {
      let lane = laneEnds.findIndex((end) => item.x > end + 12);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
      laneEnds[lane] = item.x + 170;
      return { ...item, lane };
    });
  }, [px, timeline.announcements, span]);
  const laneCount = Math.max(1, ...bands.map((band) => band.lane + 1));
  const spacerHeight = 8 + LANE * (1 + laneCount);

  const goToToday = useCallback((smooth: boolean) => {
    const element = scrollRef.current;
    if (!element || todayX == null) return;
    const left = Math.max(0, todayX - 160);
    if (typeof element.scrollTo === 'function') element.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    else element.scrollLeft = left;
  }, [todayX]);
  useEffect(() => { goToToday(false); }, [goToToday, zoom]);

  const labelFor = (item: TimelineItem) => item.labels.find((label) => label.chainId === chain?.chainId)?.label || item.labels[0]?.label || '';
  const allExpanded = groups.every((group) => expanded[group.phaseId]);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} useFlexGap flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
        <Stack direction="row" spacing={1} role="group" aria-label="Visa aktiviteter">
          {filterOptions.map((option) => (
            <ButtonBase key={option.value} aria-pressed={filter === option.value} onClick={() => setFilter(option.value)} sx={chipButtonSx(filter === option.value)}>
              {option.label}
            </ButtonBase>
          ))}
        </Stack>
        <InfoTip text={help.timelineFilter} />
        </Stack>
        <ToggleButtonGroup size="small" exclusive value={zoom} onChange={(_event, value: TimelineZoom | null) => value && setZoom(value)} aria-label="Zoom">
          <ToggleButton value="week">Vecka</ToggleButton>
          <ToggleButton value="month">Månad</ToggleButton>
          <ToggleButton value="quarter">Kvartal</ToggleButton>
        </ToggleButtonGroup>
        <InfoTip text={help.timelineZoom} />
        <Box sx={{ flex: 1 }} />
        {timeline.announcements.length > 1 ? (
          <TextField select size="small" label="Relativ vecka mot" value={chain?.chainId || ''} onChange={(event) => setChainId(event.target.value)} sx={{ minWidth: 180 }}>
            {timeline.announcements.map((item) => <MenuItem key={item.chainId} value={item.chainId}>{item.chainName}</MenuItem>)}
          </TextField>
        ) : chain && (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: launchColors.navy }}>Relativ vecka mot {chain.chainName}</Typography>
        )}
        <InfoTip text={help.timelineRelative} />
        <Button size="small" variant="outlined" startIcon={<TodayIcon />} onClick={() => goToToday(true)} disabled={todayX == null}>Gå till idag</Button>
        <Button size="small" onClick={() => setExpanded(Object.fromEntries(groups.map((group) => [group.phaseId, !allExpanded])))}>
          {allExpanded ? 'Fäll ihop alla' : 'Fäll ut alla'}
        </Button>
        <InfoTip text={help.timelinePhase} />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" component="ul" aria-label="Förklaring" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {legend.map((item) => (
          <Box component="li" key={item.status} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: launchColors.textSecondary }}>
            <Box aria-hidden sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: scheduleMarkerColor[item.status], border: '2px solid #FFFFFF', boxShadow: `0 0 0 1px ${launchColors.divider}` }} />
            {item.label}
          </Box>
        ))}
        <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: launchColors.textSecondary }}>
          <Box aria-hidden sx={{ width: 9, height: 9, border: `2px solid ${launchColors.plum}`, transform: 'rotate(45deg)', bgcolor: launchColors.card }} />
          Prognos
        </Box>
        <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: launchColors.textSecondary }}>
          <Box aria-hidden sx={{ width: 14, height: 12, bgcolor: launchColors.petrolLight, borderLeft: `2px solid ${launchColors.petrol}` }} />
          Kedjeavisering
        </Box>
        <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: launchColors.textSecondary }}>
          <Box aria-hidden sx={{ width: 2, height: 14, bgcolor: launchColors.dangerText }} />
          Idag
        </Box>
      </Stack>
      <InfoTip text={help.timelineLegend} />
      </Stack>

      <Paper
        ref={scrollRef}
        variant="outlined"
        aria-label="Horisontell lanseringskalender"
        sx={{ overflow: 'auto', maxHeight: '70vh', borderRadius: '12px', borderColor: launchColors.divider, bgcolor: launchColors.card }}
      >
        <Box sx={{ width: LABEL + plotWidth, position: 'relative' }}>
          <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 6, bgcolor: launchColors.section, borderBottom: `1px solid ${launchColors.divider}` }}>
            <Box sx={{ width: LABEL, flexShrink: 0, position: 'sticky', left: 0, zIndex: 7, bgcolor: launchColors.section, px: 1.75, display: 'flex', alignItems: 'flex-end', pb: 0.75, borderRight: `1px solid ${launchColors.divider}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary }}>Aktivitet</Typography>
            </Box>
            <Box sx={{ width: plotWidth, height: 52, position: 'relative' }} aria-hidden>
              {axis.months.map((month) => (
                <Box key={month.key} sx={{ position: 'absolute', left: month.startDay * px, width: month.days * px, top: 0, height: 24, borderLeft: `1px solid ${launchColors.divider}`, px: 1, overflow: 'hidden' }}>
                  <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, color: launchColors.navy, lineHeight: '24px' }}>{month.label}</Typography>
                </Box>
              ))}
              {axis.weeks.map((week) => {
                const width = week.days * px;
                const anchorWeek = week.relative === 0;
                const relative = week.relative == null ? '' : anchorWeek ? 'vecka 0' : `${week.relative > 0 ? '+' : '−'}${Math.abs(week.relative)}`;
                return (
                  <Box key={week.key} sx={{ position: 'absolute', left: week.startDay * px, width, top: 24, height: 28, borderLeft: `1px solid ${launchColors.divider}`, px: 0.5, overflow: 'hidden', bgcolor: anchorWeek ? launchColors.petrolLight : 'transparent' }}>
                    <Typography noWrap sx={{ fontSize: 11, lineHeight: '28px', fontWeight: anchorWeek ? 700 : 500, color: anchorWeek ? launchColors.petrolDark : launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                      {width >= 60 ? `v.${week.week}` : week.week}
                      {width >= 200 && ` · ${shortDate(week.key, timeline.asOf)}`}
                      {width >= 60 && relative && <Box component="span" sx={{ color: anchorWeek ? launchColors.petrolDark : launchColors.textMuted }}>{` · ${relative}`}</Box>}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Box aria-hidden sx={{ position: 'absolute', top: 0, bottom: 0, left: LABEL, width: plotWidth, pointerEvents: 'none', zIndex: 0 }}>
              {axis.weeks.map((week) => (
                <Box key={week.key} sx={{ position: 'absolute', top: 0, bottom: 0, left: week.startDay * px, borderLeft: `1px solid ${launchColors.neutralBg}` }} />
              ))}
              {bands.map((band) => (
                <Box key={band.chainId} sx={{ position: 'absolute', top: 0, bottom: 0, left: band.x, width: band.width, bgcolor: 'rgba(102,154,167,0.16)', borderLeft: `2px solid ${launchColors.petrol}` }} />
              ))}
            </Box>
            {todayX != null && (
              <Box aria-hidden sx={{ position: 'absolute', top: 0, bottom: 0, left: LABEL + todayX - 1, width: 2, bgcolor: launchColors.dangerText, zIndex: 2, pointerEvents: 'none' }} />
            )}

            <Box sx={{ display: 'flex', height: spacerHeight, position: 'relative', zIndex: 3 }}>
              <Box sx={{ width: LABEL, flexShrink: 0, position: 'sticky', left: 0, bgcolor: launchColors.card, borderRight: `1px solid ${launchColors.divider}`, zIndex: 4 }} />
              <Box sx={{ width: plotWidth, position: 'relative' }}>
                {todayX != null && (
                  <Typography sx={{ position: 'absolute', top: 6, left: todayX, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 700, color: '#FFFFFF', bgcolor: launchColors.dangerText, borderRadius: '10px', px: 1, lineHeight: '18px', whiteSpace: 'nowrap' }}>
                    Idag {shortDate(timeline.asOf, timeline.asOf)}
                  </Typography>
                )}
                {bands.map((band) => (
                  <Typography key={band.chainId} sx={{ position: 'absolute', top: 6 + LANE * (band.lane + 1), left: band.x + 4, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 11, fontWeight: 700, color: launchColors.petrolDark, bgcolor: launchColors.petrolLight, border: `1px solid ${launchColors.petrol}`, borderRadius: '10px', px: 0.9, lineHeight: '16px', whiteSpace: 'nowrap' }}>
                    <FlagIcon sx={{ fontSize: 12 }} />
                    {band.chainName} · avisering v.{isoWeek(band.date)}
                  </Typography>
                ))}
              </Box>
            </Box>

            {groups.map((group) => {
              const isOpen = Boolean(expanded[group.phaseId]);
              const color = phaseColor(group.phaseId);
              const regionId = `timeline-phase-${group.phaseId}`;
              return (
                <Box key={group.phaseId} sx={{ position: 'relative', zIndex: 3 }}>
                  <Box sx={{ display: 'flex', height: PHASE_ROW, borderTop: `1px solid ${launchColors.divider}` }}>
                    <ButtonBase
                      onClick={() => setExpanded((current) => ({ ...current, [group.phaseId]: !isOpen }))}
                      aria-expanded={isOpen}
                      aria-controls={regionId}
                      sx={{ width: LABEL, flexShrink: 0, position: 'sticky', left: 0, zIndex: 4, bgcolor: launchColors.card, px: 1.25, gap: 1, justifyContent: 'flex-start', borderRight: `1px solid ${launchColors.divider}`, '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: -2 } }}
                    >
                      {isOpen ? <ExpandMoreIcon fontSize="small" sx={{ color: launchColors.textSecondary }} /> : <ChevronRightIcon fontSize="small" sx={{ color: launchColors.textSecondary }} />}
                      <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
                      <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: launchColors.text, flex: 1, textAlign: 'left' }}>{displayText(group.phaseName)}</Typography>
                      <Typography sx={{ fontSize: 11, color: group.lateCount ? launchColors.dangerText : launchColors.textSecondary, fontWeight: group.lateCount ? 700 : 400, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {group.doneCount}/{group.totalCount}{group.lateCount ? ` · ${group.lateCount} sena` : ''}
                      </Typography>
                    </ButtonBase>
                    <Box aria-hidden sx={{ width: plotWidth, position: 'relative', bgcolor: isOpen ? 'transparent' : 'rgba(250,246,241,0.55)' }}>
                      {!isOpen && group.items.map((item) => {
                        const place = placeDate(item.baseline, span);
                        return place.inside ? (
                          <Box key={item.id} sx={{ position: 'absolute', left: dayCenter(place.day, px) - 2, top: 14, width: 4, height: 12, borderRadius: '2px', bgcolor: item.status === 'overdue' ? launchColors.dangerText : color, opacity: 0.75 }} />
                        ) : null;
                      })}
                    </Box>
                  </Box>
                  {isOpen && (
                    <Box id={regionId} role="group" aria-label={displayText(group.phaseName)}>
                      {group.items.length === 0 && (
                        <Box sx={{ display: 'flex', height: ACTIVITY_ROW }}>
                          <Box sx={{ width: LABEL, flexShrink: 0, position: 'sticky', left: 0, zIndex: 4, bgcolor: launchColors.card, borderLeft: `3px solid ${color}`, borderRight: `1px solid ${launchColors.divider}`, display: 'flex', alignItems: 'center', pl: 4 }}>
                            <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, fontStyle: 'italic' }}>Inga daterade aktiviteter</Typography>
                          </Box>
                        </Box>
                      )}
                      {group.items.map((item) => (
                        <TimelineRow
                          key={item.id}
                          item={item}
                          color={color}
                          px={px}
                          plotWidth={plotWidth}
                          span={span}
                          asOf={timeline.asOf}
                          roleText={item.roleText}
                          relativeLabel={labelFor(item)}
                          onOpen={() => onOpenActivity(item.id)}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {timeline.undated.length > 0 && (
        <Box component="section" aria-labelledby="timeline-undated-heading">
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
            <Typography id="timeline-undated-heading" component="h3" sx={{ fontSize: 16, fontWeight: 700, color: launchColors.navy }}>
              Aktiviteter utan datum ({timeline.undated.length})
            </Typography>
            <InfoTip text={help.timelineUndated} />
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {timeline.undated.map((item) => (
              <ButtonBase
                key={item.id}
                onClick={() => onOpenActivity(item.id)}
                sx={{ display: 'block', textAlign: 'left', p: 1.25, borderRadius: '10px', border: `1px dashed ${launchColors.textMuted}`, bgcolor: launchColors.card, '&:hover': { bgcolor: launchColors.section }, '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}` } }}
              >
                <Typography component="span" sx={{ display: 'block', fontSize: 12, color: launchColors.textSecondary }}>{displayText(item.phaseName)}: </Typography>
                <Typography component="span" sx={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{displayText(item.title)}</Typography>
                <Typography component="span" sx={{ display: 'block', fontSize: 12, color: launchColors.navy, mt: 0.5 }}>Sätt datum →</Typography>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ position: 'relative' }}>
      <Accordion disableGutters sx={{ borderRadius: '12px', border: `1px solid ${launchColors.divider}`, boxShadow: 'none', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="timeline-table-content">
          <Typography>Tillgänglig tabellvy</Typography>
        </AccordionSummary>
        <AccordionDetails id="timeline-table-content" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Tidslinje som tabell">
            <TableHead><TableRow><TableCell>Fas</TableCell><TableCell>Aktivitet</TableCell><TableCell>Datumstatus</TableCell><TableCell>Grundplan</TableCell><TableCell>Prognos</TableCell><TableCell>Relativ tid</TableCell></TableRow></TableHead>
            <TableBody>
              {groups.flatMap((group) => group.items.map((item) => (
                <TableRow key={item.id} hover tabIndex={0} onClick={() => onOpenActivity(item.id)} onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpenActivity(item.id);
                  }
                }} sx={{ cursor: 'pointer' }}>
                  <TableCell>{displayText(group.phaseName)}</TableCell>
                  <TableCell>{displayText(item.title)}</TableCell>
                  <TableCell>{scheduleStatusText[item.status]}</TableCell>
                  <TableCell>{formatDate(item.baseline)}</TableCell>
                  <TableCell>{item.forecast ? formatDate(item.forecast) : '–'}</TableCell>
                  <TableCell>{labelFor(item)}</TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
      <Box sx={{ position: 'absolute', top: 8, right: 48, zIndex: 2 }}>
        <InfoTip text={help.timelineTable} />
      </Box>
      </Box>
    </Stack>
  );
};

interface RowProps {
  item: TimelineItem;
  color: string;
  px: number;
  plotWidth: number;
  span: ReturnType<typeof timelineWindow>;
  asOf: string;
  roleText: string;
  relativeLabel: string;
  onOpen: () => void;
}

const TimelineRow: React.FC<RowProps> = ({ item, color, px, plotWidth, span, asOf, roleText, relativeLabel, onOpen }) => {
  const title = displayText(item.title);
  const baseline = placeDate(item.baseline, span);
  const forecast = item.forecast && item.forecast !== item.baseline ? placeDate(item.forecast, span) : null;
  const markerColor = scheduleMarkerColor[item.status];
  const statusText = scheduleStatusText[item.status];
  const baselineLabel = `${title}, grundplan ${formatDate(item.baseline)}, ${statusText}${relativeLabel ? `, ${relativeLabel}` : ''}`;
  const baselineX = baseline.inside ? dayCenter(baseline.day, px) : baseline.side === 'before' ? 0 : plotWidth;
  const forecastX = forecast ? (forecast.inside ? dayCenter(forecast.day, px) : forecast.side === 'before' ? 0 : plotWidth) : null;
  const duration = item.duration
    ? { start: placeDate(item.duration.start, span), end: placeDate(item.duration.end, span) }
    : null;
  const durationStart = duration ? (duration.start.inside ? duration.start.day * px : duration.start.side === 'before' ? 0 : plotWidth) : 0;
  const durationEnd = duration ? (duration.end.inside ? (duration.end.day + 1) * px : duration.end.side === 'before' ? 0 : plotWidth) : 0;

  return (
    <Box sx={{
      display: 'flex',
      height: ACTIVITY_ROW,
      '& .timeline-row-label, & .timeline-row-plot': { boxShadow: `inset 0 -1px 0 ${launchColors.rowDivider}` },
      '&:last-child .timeline-row-label, &:last-child .timeline-row-plot': { boxShadow: 'none' },
      '&:hover .timeline-row-label': { bgcolor: launchColors.section },
    }}>
      <Tooltip title={title} placement="top-start" enterDelay={400}>
        <ButtonBase
          component="div"
          className="timeline-row-label"
          onClick={onOpen}
          aria-label={`Öppna ${title}`}
          sx={{
            width: LABEL,
            flexShrink: 0,
            position: 'sticky',
            left: 0,
            zIndex: 4,
            bgcolor: launchColors.card,
            borderLeft: `3px solid ${color}`,
            borderRight: `1px solid ${launchColors.divider}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pl: 4,
            pr: 1.5,
            textAlign: 'left',
            '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: -2 },
          }}
        >
          <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: launchColors.text, width: '100%' }}>{title}</Typography>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <DepartmentBadge name={roleText} size={16} nameSize={11} />
          </Box>
        </ButtonBase>
      </Tooltip>
      <Box className="timeline-row-plot" sx={{ width: plotWidth, position: 'relative' }}>
        {duration && durationEnd > durationStart && (
          <Box aria-hidden sx={{ position: 'absolute', left: durationStart, width: durationEnd - durationStart, top: 15, height: 6, borderRadius: 3, bgcolor: color, opacity: 0.25 }} />
        )}
        {forecastX != null && (
          <Box aria-hidden sx={{ position: 'absolute', left: Math.min(baselineX, forecastX), width: Math.abs(forecastX - baselineX), top: 17, borderTop: `2px dashed ${launchColors.plum}` }} />
        )}
        {baseline.inside ? (
          <Tooltip title={`${title} · ${shortDate(item.baseline, asOf)} · ${statusText}`}>
            <ButtonBase
              onClick={onOpen}
              aria-label={baselineLabel}
              sx={{
                position: 'absolute',
                left: baselineX - 8,
                top: 10,
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: markerColor,
                border: '2px solid #FFFFFF',
                boxShadow: `0 0 0 1px ${markerColor}`,
                color: '#FFFFFF',
                zIndex: 1,
                '&:hover, &.Mui-focusVisible': { transform: 'scale(1.25)', outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
              }}
            >
              {item.status === 'complete' && <CheckIcon sx={{ fontSize: 11 }} />}
            </ButtonBase>
          </Tooltip>
        ) : (
          <ButtonBase
            onClick={onOpen}
            aria-label={baselineLabel}
            sx={{
              position: 'absolute',
              top: 9,
              ...(baseline.side === 'before' ? { left: 6 } : { right: 6 }),
              fontSize: 11,
              fontWeight: 700,
              color: item.status === 'overdue' ? launchColors.dangerText : launchColors.textSecondary,
              bgcolor: item.status === 'overdue' ? launchColors.dangerBg : launchColors.neutralBg,
              borderRadius: '9px',
              px: 1,
              lineHeight: '18px',
              whiteSpace: 'nowrap',
              zIndex: 1,
              '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
            }}
          >
            {baseline.side === 'before'
              ? `◂ ${shortDate(item.baseline, asOf)} · ${item.status === 'complete' ? 'klar' : relativeDue(item.baseline, asOf)}`
              : `${shortDate(item.baseline, asOf)} ▸`}
          </ButtonBase>
        )}
        {forecast?.inside && forecastX != null && (
          <Tooltip title={`Prognos ${shortDate(item.forecast, asOf)}`}>
            <ButtonBase
              onClick={onOpen}
              aria-label={`${title}, prognos ${formatDate(item.forecast)}`}
              sx={{
                position: 'absolute',
                left: forecastX - 6,
                top: 12,
                width: 12,
                height: 12,
                transform: 'rotate(45deg)',
                border: `2px solid ${launchColors.plum}`,
                bgcolor: launchColors.card,
                zIndex: 1,
                '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 2 },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default TimelineView;
